/**
 * React Query Hooks for Output Schemas
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { outputSchemasAdminApi, outputSchemasApi } from '@/lib/api/outputSchemas';
import type {
  OutputSchema,
  OutputSchemaCreate,
  OutputSchemaUpdate,
  OutputSchemaListResponse,
  OutputSchemaListItemsResponse,
  OutputSchemaListParams,
} from '@/types/outputSchema';

// ============================================================================
// Query Keys
// ============================================================================

export const OUTPUT_SCHEMA_QUERY_KEYS = {
  all: ['output-schemas'] as const,
  list: (params?: OutputSchemaListParams) => ['output-schemas', 'list', params] as const,
  detail: (key: string) => ['output-schemas', 'detail', key] as const,
  forAgent: (agentType: string) => ['output-schemas', 'for-agent', agentType] as const,
};

// ============================================================================
// Admin Hooks (CRUD)
// ============================================================================

/**
 * Hook to list all output schemas (admin)
 */
export function useOutputSchemas(
  params?: OutputSchemaListParams
): UseQueryResult<OutputSchemaListResponse, Error> {
  return useQuery({
    queryKey: OUTPUT_SCHEMA_QUERY_KEYS.list(params),
    queryFn: () => outputSchemasAdminApi.list(params),
  });
}

/**
 * Hook to get a single output schema by key (admin)
 */
export function useOutputSchema(key: string): UseQueryResult<OutputSchema, Error> {
  return useQuery({
    queryKey: OUTPUT_SCHEMA_QUERY_KEYS.detail(key),
    queryFn: () => outputSchemasAdminApi.get(key),
    enabled: !!key,
  });
}

/**
 * Hook to create a new output schema
 */
export function useCreateOutputSchema(): UseMutationResult<
  OutputSchema,
  Error,
  OutputSchemaCreate
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OutputSchemaCreate) => outputSchemasAdminApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: OUTPUT_SCHEMA_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook to update an existing output schema
 */
export function useUpdateOutputSchema(): UseMutationResult<
  OutputSchema,
  Error,
  { key: string; data: OutputSchemaUpdate }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, data }) => outputSchemasAdminApi.update(key, data),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: OUTPUT_SCHEMA_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: OUTPUT_SCHEMA_QUERY_KEYS.detail(key) });
    },
  });
}

/**
 * Hook to delete an output schema
 */
export function useDeleteOutputSchema(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => outputSchemasAdminApi.delete(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: OUTPUT_SCHEMA_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook to toggle the enabled status of an output schema
 */
export function useToggleOutputSchema(): UseMutationResult<
  OutputSchema,
  Error,
  { key: string; enabled: boolean }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, enabled }) => outputSchemasAdminApi.toggle(key, enabled),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: OUTPUT_SCHEMA_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: OUTPUT_SCHEMA_QUERY_KEYS.detail(key) });
    },
  });
}

// ============================================================================
// Public Hooks (Read-only)
// ============================================================================

/**
 * Hook to list all enabled output schemas (public)
 */
export function useAvailableOutputSchemas(
  params?: { limit?: number; offset?: number }
): UseQueryResult<OutputSchemaListItemsResponse, Error> {
  return useQuery({
    queryKey: ['output-schemas', 'available', params] as const,
    queryFn: () => outputSchemasApi.list(params),
  });
}

/**
 * Hook to get schemas available to a specific agent
 */
export function useOutputSchemasForAgent(
  agentType: string | undefined
): UseQueryResult<OutputSchemaListItemsResponse, Error> {
  return useQuery({
    queryKey: OUTPUT_SCHEMA_QUERY_KEYS.forAgent(agentType || ''),
    queryFn: () => outputSchemasApi.getForAgent(agentType!),
    enabled: !!agentType,
  });
}
